import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/component/ds/utils';

/* shadcn's Alert (radix base, Vega style), ported to Tailwind v3 + the `ds-` prefix. */

const alertVariants = cva(
  [
    'ds-group/alert ds-relative ds-grid ds-w-full ds-gap-0.5 ds-rounded-lg ds-border ds-px-4 ds-py-3 ds-text-left ds-text-sm',
    'has-[[data-slot=alert-action]]:ds-relative has-[[data-slot=alert-action]]:ds-pr-[72px]',
    'has-[>svg]:ds-grid-cols-[auto_1fr] has-[>svg]:ds-gap-x-2.5',
    "[&>svg]:ds-row-span-2 [&>svg]:ds-translate-y-0.5 [&>svg]:ds-text-current [&>svg:not([class*='size-'])]:ds-size-4"
  ],
  {
    variants: {
      variant: {
        default: 'ds-bg-card ds-text-card-foreground',
        destructive: 'ds-bg-card ds-text-destructive [&>[data-slot=alert-description]]:ds-text-destructive [&>svg]:ds-text-current'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

function Alert ({
  className,
  variant,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot='alert'
      role='alert'
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot='alert-title'
      className={cn(
        'ds-font-medium group-has-[>svg]/alert:ds-col-start-2',
        '[&_a]:ds-underline [&_a]:ds-underline-offset-[3px] [&_a:hover]:ds-text-foreground',
        className
      )}
      {...props}
    />
  );
}

function AlertDescription ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot='alert-description'
      className={cn(
        'ds-text-sm ds-text-muted-foreground ds-text-balance [&_p:not(:last-child)]:ds-mb-4',
        '[&_a]:ds-underline [&_a]:ds-underline-offset-[3px] [&_a:hover]:ds-text-foreground',
        className
      )}
      {...props}
    />
  );
}

function AlertAction ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot='alert-action'
      className={cn('ds-absolute ds-right-3 ds-top-2.5', className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, AlertAction };
